import * as React from "react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { cn } from "src/shadcn/lib/utils";

import { RadioGroup as ShadcnRadioGroup } from "src/shadcn/components/radio-group";

// `relative` contains the items' hidden form inputs (Radix renders a
// visually-hidden `position: absolute` input as a sibling of each item for
// native form submission). The Field wrapper already does this when the group
// has a label/error/helpText, but a bare group renders without one; making
// the group root a positioning context covers that path too. Applied here
// (not the CLI-generated shadcn component) so it survives shadcn CLI upgrades.
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof ShadcnRadioGroup>,
  React.ComponentProps<typeof ShadcnRadioGroup>
>(({ className, ...props }, ref) => (
  <ShadcnRadioGroup
    ref={ref}
    className={cn("relative", className)}
    {...props}
  />
));

RadioGroup.displayName = "RadioGroup";

/**
 * RadioGroupItem re-composed from Radix primitives with the inner dot
 * using flex centering instead of absolute + translate, fixing RTL alignment.
 */
const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentProps<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    data-slot="radio-group-item"
    className={cn(
      "group/radio-group-item peer relative flex aspect-square size-4 shrink-0 cursor-pointer rounded-full border border-neutral-400 outline-none after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-primary dark:border-neutral-500 dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary",
      className
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator
      data-slot="radio-group-indicator"
      className="flex size-full items-center justify-center"
    >
      <span className="size-2 rounded-full bg-primary-foreground" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));

RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
